import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getDeploymentService,
  getServiceLogs,
  setServiceOwner,
  uploadServiceLogo,
  deleteServiceLogo,
  listMembers,
  triggerDeploy,
  cancelDeploy,
  restartService,
  rollbackService,
  getMe,
  ApiError,
  type Deploy,
  type RenderService,
} from "@/lib/api";
import { deployStatus, fmtDuration, timeAgo } from "@/lib/deployments";
import { STATUS_HUE } from "@/lib/statusColors";
import { EditableLogo } from "@/components/EntityLogo";
import { PlatformUsage } from "./PlatformUsage";
import { UserSessions } from "./UserSessions";

export type Tab = "deploys" | "logs" | "usage" | "sessions";

/**
 * One Render service: its actions, deploys and logs, its platform usage
 * when that's counted (`usage`), and who signs in to it when it's a PE app
 * (`sessions`, admins only). Shown inside its deployment's page; keyed by
 * service there, so switching services starts clean. The tab is the page's,
 * so it holds when you switch.
 */
export function ServiceDetail({
  serviceId,
  usage,
  sessions: hasSessions,
  tab: chosenTab,
  onTabChange: setTab,
}: {
  serviceId: string;
  usage: boolean;
  sessions: boolean;
  tab: Tab;
  onTabChange: (tab: Tab) => void;
}) {
  const [actionError, setActionError] = useState<string | null>(null);
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const sessions = hasSessions && me?.role === "admin";
  // A tab chosen on the app, then Keycloak picked: show its deploys.
  const tab = (chosenTab === "usage" && !usage) || (chosenTab === "sessions" && !sessions) ? "deploys" : chosenTab;

  const { data, isLoading } = useQuery({
    queryKey: ["deployments", "service", serviceId],
    queryFn: () => getDeploymentService(serviceId),
    // Poll fast while a deploy is running so status flips live, like Render.
    refetchInterval: (q) =>
      q.state.data?.deploys.some((d) => d.inProgress) ? 5_000 : 30_000,
  });

  const liveDeploy = data?.deploys.find((d) => d.inProgress) ?? null;

  return (
    <div>
      {isLoading && (
        <div className="py-16 text-center text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-xl" />
        </div>
      )}

      {data && (
        <>
          <ServiceHeader
            serviceId={serviceId}
            service={data.service}
            logoUrl={data.logoUrl}
            ownerId={data.ownerId}
            latest={data.deploys[0] ?? null}
            liveDeploy={liveDeploy}
            onError={setActionError}
          />
          {actionError && (
            <p className="mb-4 rounded-token-md bg-danger/10 px-3 py-2 text-sm text-danger">
              <i className="fa-solid fa-triangle-exclamation mr-1.5" />
              {actionError}
            </p>
          )}

          <div className="min-w-0">
            {/* Tabs */}
            <div className="mb-4 flex gap-1 border-b border-border">
              {(
                [
                  { key: "deploys", label: "Deploys", icon: "fa-rocket" },
                  { key: "logs", label: "Logs", icon: "fa-terminal" },
                  ...(usage ? [{ key: "usage", label: "Platform usage", icon: "fa-chart-column" }] : []),
                  ...(sessions ? [{ key: "sessions", label: "Sessions", icon: "fa-user-clock" }] : []),
                ] as { key: Tab; label: string; icon: string }[]
              ).map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                    tab === t.key
                      ? "border-accent text-text-heading"
                      : "border-transparent text-text-secondary hover:text-text-heading"
                  }`}
                >
                  <i className={`fa-solid ${t.icon} mr-1.5 text-xs`} />
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "deploys" ? (
              <DeployList
                serviceId={serviceId}
                deploys={data.deploys}
                repo={data.service.repo}
                onError={setActionError}
              />
            ) : tab === "logs" ? (
              <LogsPanel serviceId={serviceId} />
            ) : tab === "usage" ? (
              <PlatformUsage serviceId={serviceId} />
            ) : (
              <UserSessions serviceId={serviceId} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Header: name + status + actions + owner and links
   ════════════════════════════════════════════════════════════════════════ */
function ServiceHeader({
  serviceId,
  service,
  logoUrl,
  ownerId,
  latest,
  liveDeploy,
  onError,
}: {
  serviceId: string;
  service: RenderService;
  logoUrl: string | null;
  ownerId: string | null;
  latest: Deploy | null;
  liveDeploy: Deploy | null;
  onError: (msg: string | null) => void;
}) {
  const qc = useQueryClient();
  const { data: members } = useQuery({ queryKey: ["members"], queryFn: listMembers });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["deployments", "service", serviceId] });
    qc.invalidateQueries({ queryKey: ["deployments", "groups"] });
  };
  const fail = (e: unknown) =>
    onError(e instanceof ApiError ? e.message : "Action failed — check Render");

  const logoUpload = useMutation({
    mutationFn: (file: File) => uploadServiceLogo(serviceId, file),
    onSuccess: invalidate,
    onError: fail,
  });
  const logoRemove = useMutation({
    mutationFn: () => deleteServiceLogo(serviceId),
    onSuccess: invalidate,
    onError: fail,
  });
  const owner = useMutation({
    mutationFn: (next: string | null) => setServiceOwner(serviceId, next),
    onSuccess: invalidate,
    onError: fail,
  });

  const deploy = useMutation({
    mutationFn: () => triggerDeploy(serviceId),
    onSuccess: () => {
      onError(null);
      invalidate();
    },
    onError: fail,
  });
  const restart = useMutation({
    mutationFn: () => restartService(serviceId),
    onSuccess: () => {
      onError(null);
      invalidate();
    },
    onError: fail,
  });

  const st = latest ? deployStatus(latest.status) : null;

  return (
    <header className="mb-5">
      <div className="flex flex-wrap items-center gap-3">
        <EditableLogo
          name={service.name}
          url={logoUrl}
          size={36}
          fallbackIcon="fa-server"
          uploading={logoUpload.isPending}
          onUpload={(f) => logoUpload.mutate(f)}
          onRemove={() => logoRemove.mutate()}
        />
        <h2 className="page-title">{service.name}</h2>
        {service.suspended ? (
          <StatusPill color={STATUS_HUE.neutral} label="Suspended" />
        ) : (
          st && latest && (
            <StatusPill color={st.color} label={st.label} pulse={latest.inProgress} />
          )
        )}

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => deploy.mutate()}
            disabled={deploy.isPending || !!liveDeploy}
            title={liveDeploy ? "A deploy is already running" : "Deploy the latest commit"}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ color: "var(--color-on-accent)" }}
          >
            <i
              className={`fa-solid ${deploy.isPending ? "fa-circle-notch fa-spin" : "fa-rocket"} mr-1.5 text-xs`}
            />
            Manual deploy
          </button>
          <button
            onClick={() => {
              if (confirm(`Restart ${service.name}?`)) restart.mutate();
            }}
            disabled={restart.isPending}
            className="rounded-token-md border border-border px-3 py-2 text-sm text-text-secondary transition-colors hover:border-accent hover:text-text-heading disabled:opacity-50"
          >
            <i
              className={`fa-solid ${restart.isPending ? "fa-circle-notch fa-spin" : "fa-rotate-right"} mr-1.5 text-xs`}
            />
            Restart
          </button>
          {service.dashboardUrl && (
            <a
              href={service.dashboardUrl}
              target="_blank"
              rel="noreferrer"
              className="rounded-token-md border border-border px-3 py-2 text-sm text-text-secondary transition-colors hover:border-accent hover:text-text-heading"
            >
              Render
              <i className="fa-solid fa-arrow-up-right-from-square ml-1.5 text-xs" />
            </a>
          )}
        </div>
      </div>

      {/* owner and links */}
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <label
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-bg-card px-2.5 py-1 text-text-secondary"
          title="Service owner"
        >
          <i className="fa-solid fa-user-shield text-[0.6rem]" />
          <select
            className="bg-transparent text-xs text-text-secondary outline-none"
            value={ownerId ?? ""}
            onChange={(e) => owner.mutate(e.target.value || null)}
          >
            <option value="">No owner</option>
            {members?.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        {service.repo && (
          <a
            href={service.repo}
            target="_blank"
            rel="noreferrer"
            className="rounded-full border border-border bg-bg-card px-2.5 py-1 text-text-secondary transition-colors hover:border-accent hover:text-accent"
          >
            <i className="fa-brands fa-github mr-1" />
            {service.repo.replace(/^https?:\/\/(www\.)?github\.com\//, "")}
          </a>
        )}
        {service.url && (
          <a
            href={service.url}
            target="_blank"
            rel="noreferrer"
            className="rounded-full border border-border bg-bg-card px-2.5 py-1 text-accent transition-colors hover:border-accent"
          >
            <i className="fa-solid fa-globe mr-1" />
            {service.url.replace(/^https?:\/\//, "")}
          </a>
        )}
      </div>
    </header>
  );
}

export function StatusPill({
  color,
  label,
  pulse,
}: {
  color: string;
  label: string;
  pulse?: boolean;
}) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ background: `${color}18`, color, border: `1px solid ${color}40` }}
    >
      <span
        className={`h-2 w-2 rounded-full ${pulse ? "animate-pulse" : ""}`}
        style={{ background: color }}
      />
      {label}
    </span>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Deploys
   ════════════════════════════════════════════════════════════════════════ */
function DeployList({
  serviceId,
  deploys,
  repo,
  onError,
}: {
  serviceId: string;
  deploys: Deploy[];
  repo: string | null;
  onError: (msg: string | null) => void;
}) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["deployments", "service", serviceId] });
  const fail = (e: unknown) =>
    onError(e instanceof ApiError ? e.message : "Action failed — check Render");

  const cancel = useMutation({
    mutationFn: (deployId: string) => cancelDeploy(serviceId, deployId),
    onSuccess: () => {
      onError(null);
      invalidate();
    },
    onError: fail,
  });
  const rollback = useMutation({
    mutationFn: (deployId: string) => rollbackService(serviceId, deployId),
    onSuccess: () => {
      onError(null);
      invalidate();
    },
    onError: fail,
  });

  return (
    <div className="flex flex-col gap-2">
      {deploys.map((d, i) => {
        const st = deployStatus(d.status);
        const canRollback =
          i > 0 && !d.inProgress && (d.status === "live" || d.status === "deactivated");
        return (
          <div
            key={d.id}
            className="group flex items-start justify-between gap-4 rounded-token-md border border-border bg-bg-card p-4"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${d.inProgress ? "animate-pulse" : ""}`}
                  style={{ background: st.color }}
                />
                <span className="text-sm font-medium" style={{ color: st.color }}>
                  {st.label}
                </span>
                {d.commitId &&
                  (repo ? (
                    <a
                      href={`${repo}/commit/${d.commitFullId ?? d.commitId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-xs text-text-muted hover:text-accent hover:underline"
                    >
                      {d.commitId}
                    </a>
                  ) : (
                    <span className="font-mono text-xs text-text-muted">{d.commitId}</span>
                  ))}
                {d.durationSecs != null && (
                  <span className="text-xs text-text-muted">
                    <i className="fa-regular fa-clock mr-1" />
                    {fmtDuration(d.durationSecs)}
                  </span>
                )}
              </div>
              <p className="mt-1 truncate text-sm text-text-secondary">
                {d.commitMessage ?? "Deploy"}
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              <div className="text-right text-xs text-text-muted">
                {timeAgo(d.finishedAt ?? d.createdAt)}
                {d.trigger && <div className="mt-0.5">{d.trigger.replace(/_/g, " ")}</div>}
              </div>
              {d.inProgress && (
                <button
                  onClick={() => cancel.mutate(d.id)}
                  disabled={cancel.isPending}
                  className="rounded-md border border-border px-2.5 py-1.5 text-xs text-text-secondary transition-colors hover:border-danger hover:text-danger disabled:opacity-50"
                >
                  {cancel.isPending ? "Canceling…" : "Cancel"}
                </button>
              )}
              {canRollback && (
                <button
                  onClick={() => {
                    if (
                      confirm(
                        `Roll back to "${d.commitMessage ?? d.commitId ?? "this deploy"}"? This redeploys that build.`,
                      )
                    )
                      rollback.mutate(d.id);
                  }}
                  disabled={rollback.isPending}
                  className="rounded-md border border-border px-2.5 py-1.5 text-xs text-text-secondary opacity-0 transition-all hover:border-accent hover:text-accent group-hover:opacity-100 disabled:opacity-50"
                >
                  <i className="fa-solid fa-clock-rotate-left mr-1" />
                  Rollback
                </button>
              )}
            </div>
          </div>
        );
      })}
      {deploys.length === 0 && <p className="text-sm text-text-muted">No deploys yet.</p>}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Logs
   ════════════════════════════════════════════════════════════════════════ */
function LogsPanel({ serviceId }: { serviceId: string }) {
  const [follow, setFollow] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: logs, isLoading, isError, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ["deployments", "logs", serviceId],
    queryFn: () => getServiceLogs(serviceId, 200),
    refetchInterval: follow ? 5_000 : false,
  });

  // Newest-first from the API; render oldest → newest and pin to the bottom.
  const ordered = [...(logs ?? [])].reverse();
  useEffect(() => {
    if (follow && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [dataUpdatedAt, follow]);

  const levelColor = (labels: Record<string, string>): string | undefined => {
    const level = (labels.level ?? labels.type ?? "").toLowerCase();
    if (level.includes("error")) return STATUS_HUE.red;
    if (level.includes("warn")) return STATUS_HUE.amber;
    return undefined;
  };

  return (
    <div className="overflow-hidden rounded-token-lg border border-border">
      <div className="flex items-center gap-3 border-b border-border bg-bg-card px-4 py-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          Recent logs
        </span>
        {isFetching && <i className="fa-solid fa-circle-notch fa-spin text-xs text-text-muted" />}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setFollow((f) => !f)}
            className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
              follow
                ? "bg-accent-muted text-accent"
                : "text-text-muted hover:text-text-heading"
            }`}
            title="Auto-refresh every 5s and stick to the newest line"
          >
            <i className="fa-solid fa-angles-down mr-1" />
            Follow
          </button>
          <button
            onClick={() => void refetch()}
            className="rounded-md px-2.5 py-1 text-xs text-text-muted transition-colors hover:text-text-heading"
          >
            <i className="fa-solid fa-rotate-right mr-1" />
            Refresh
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="h-[28rem] overflow-y-auto px-4 py-3 font-mono text-xs leading-relaxed"
        // The log console is intentionally dark in both themes - it reads as a
        // terminal, not as app chrome. Values are the dark-theme tokens.
        style={{ background: "#0a0e17", color: "#e2e8f0" }}
      >
        {isLoading ? (
          <p className="text-text-muted">Loading logs…</p>
        ) : isError ? (
          <p style={{ color: STATUS_HUE.red }}>
            Could not load logs from Render — the API key may lack log access.
          </p>
        ) : ordered.length === 0 ? (
          <p className="text-text-muted">No recent log lines.</p>
        ) : (
          ordered.map((l) => (
            <div key={l.id} className="flex gap-3 whitespace-pre-wrap break-all">
              {l.timestamp && (
                <span className="shrink-0 select-none opacity-40">
                  {new Date(l.timestamp).toLocaleTimeString()}
                </span>
              )}
              <span style={{ color: levelColor(l.labels) }}>{l.message}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
